<template>
    <kiosk-layout formContainer>
        <page-loading v-if="loading"></page-loading>
        <div v-else>
            <page-header class="mb-6 lg:mb-12">
                <template v-slot:subtitle>
                    <a href="/kiosk/org-units" class="no-underline! text-sky-600! text-base font-normal">
                        <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                        Search
                    </a>
                </template>

                {{ orgUnit.name }}

                <template v-slot:actions>
                    <vx-button
                        color="primary"
                        :href="
                            team
                                ? `/spark/kiosk/users/impersonate/${team.owner_id}?team_id=${orgUnit.team_id}&org_unit_id=${orgUnit.id}`
                                : '#'
                        "
                        :disabled="!team || team.owner_id === currentUserId || team.status === 'closed'"
                    >
                        Impersonate Owner <font-awesome-icon widthAuto :icon="faMasksTheater"></font-awesome-icon>
                    </vx-button>
                </template>
            </page-header>

            <div class="grid grid-cols-12">
                <page-sidebar :links="navigationLinks"></page-sidebar>

                <slot></slot>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import { faArrowLeft, faMasksTheater } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { onMounted, computed } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { VxButton } from '@voxie/frontend-components';
import { useOrgUnitDetails } from '../composables/useOrgUnitDetails';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const { mount, orgUnit, loading, team } = useOrgUnitDetails();

const currentUserId = Spark.state.user.id;

const navigationLinks = computed(() => {
    const links = [
        { href: `/kiosk/teams/${props.teamId}/org-units/${props.orgUnitId}`, label: 'Details' },
        { href: `/kiosk/teams/${props.teamId}/org-units/${props.orgUnitId}/configuration`, label: 'Configuration' },
    ];

    // Wait for team to load if we need team data for billing decisions
    if (!team.value && loading.value) {
        return links; // Return basic links while loading
    }

    // Use team's feature flags instead of current user's
    // eslint-disable-next-line no-restricted-syntax
    const teamHasNewBilling = team.value?.team_attributes?.new_billing_implementation || false;
    if (teamHasNewBilling) {
        // Show billing config for ALL org units (where they can toggle billable status)
        links.push({
            href: `/kiosk/teams/${props.teamId}/org-units/${props.orgUnitId}/billing-config`,
            label: 'Billing Config',
        });

        // Show other billing pages only for billable org units
        if (orgUnit.value?.is_billable) {
            links.push({
                href: `/kiosk/teams/${props.teamId}/org-units/${props.orgUnitId}/billing-details`,
                label: 'Billing Details',
            });

            // Only show proforma invoices if team is not closed (align with KioskTeamDetails)
            if (team.value?.status !== 'closed') {
                links.push({
                    href: `/kiosk/teams/${props.teamId}/org-units/${props.orgUnitId}/proforma-invoices`,
                    label: 'Proforma Invoices',
                });
            }

            links.push({
                href: `/kiosk/teams/${props.teamId}/org-units/${props.orgUnitId}/historical-invoices`,
                label: 'Historical Invoices',
            });
        }
    }

    return links;
});

onMounted(() => {
    mount(props.teamId, props.orgUnitId);
});
</script>
